import React from 'react';
import Tooltip from '@/components/ui/tooltip';

import { Exam, Globe } from '@phosphor-icons/react';
import {
  useIsSingleExamScope,
  useScopedPrimaryExam,
} from '../../Performance/performanceScope';
import {
  ToggleButton,
  ToggleButtonGroup,
} from '@/components/ui/toggle-button-group';

interface ExamGlobalToggleProps {
  value: 'exam' | 'global';
  onChange: (value: 'exam' | 'global') => void;
  /**
   * Names this toggle for the chat screen snapshot (ORA-633). Every widget
   * owns its own toggle, so the label has to say which widget's scope it is —
   * e.g. "Study Overview scope". The two buttons are icon-only and their
   * pressed state is not something the snapshot walker reads, so the selected
   * option is published as an annotation on the group instead. Omitted where
   * the toggle is not on a captured surface (the companion's own drawer).
   */
  scopeLabel?: string;
}

const ExamGlobalToggle: React.FC<ExamGlobalToggleProps> = ({
  value,
  onChange,
  scopeLabel,
}) => {
  const primaryExam = useScopedPrimaryExam();
  // A single-exam (NCLEX-RN) user has no "All exams" scope to switch to.
  const isSingleExamScope = useIsSingleExamScope();

  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    nextValue: 'exam' | 'global' | null
  ) => {
    if (!nextValue) return;
    onChange(nextValue);
  };

  if (isSingleExamScope) return null;

  const toggle = (
    <ToggleButtonGroup
      className="exam-global-toggle"
      value={value}
      exclusive
      onChange={handleChange}
      aria-label="Exam or Global toggle"
      size="small"
      data-chat-label={scopeLabel}
      data-chat-value={
        scopeLabel === undefined
          ? undefined
          : value === 'exam'
            ? (primaryExam?.name ?? 'Exam')
            : 'All exams'
      }
    >
      <Tooltip title={primaryExam?.name ?? 'Exam'}>
        <ToggleButton value="exam" aria-label="Exam view" size="icon">
          <Exam weight={value === 'exam' ? 'fill' : 'regular'} />
        </ToggleButton>
      </Tooltip>
      <Tooltip title="All Exams">
        <ToggleButton value="global" aria-label="Global view" size="icon">
          <Globe weight={value === 'global' ? 'fill' : 'regular'} />
        </ToggleButton>
      </Tooltip>
    </ToggleButtonGroup>
  );

  return toggle;
};

export default ExamGlobalToggle;
